import { config } from '@uhg-abyss/web/tools/config';
import { storage } from '@uhg-abyss/web/tools/storage';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { isEmpty } from 'lodash';
import find from 'lodash/find';
import React, { useEffect, useRef, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';

import {
  Constants,
  OPTUM_CORRELATION_ID_HEADER,
} from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { StyledDiv } from '../../../../common/ConstantsStyles';
import { getFeatureFlag } from '../../../../common/Utils/getFeatureFlag';
import {
  useEducationalContentStatus,
  useSaveEducationalContentStatus,
} from '../../../../hooks/useEducationalContent';
import { useHsidUuid } from '../../../../hooks/useHsidUuid';
import { useLagoon } from '../../../../hooks/useLagoon';
import { StoreKeys } from '../../../../hooks/useStore/state';
import { useStore } from '../../../../hooks/useStore/useStore';
import { DateTime } from '../../../../utils/DateTime';
import {
  PopoverKey,
  PriorityItem,
  updatePopoverPriority,
} from '../../../../utils/priorityPopover';
import {
  educationalContentModalInfoStyles,
  educationalContentModalStyles,
} from './EducationalContent.styled';

export const EducationalContentModal = () => {
  const [isSuperUser, setIsSuperUser] = useState(
    storage.session.get(Constants.STORAGE_KEYS.SESSION.SUPER_USER) || false
  );

  const [isOpen, setIsOpen] = useState(true);
  const featureFlags = useLagoon('feature-flags')();
  const isPopOverMessageEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_POPOVER_PRIORITY_FLOW
  );
  const [, setEducationalContentStatus] = useSaveEducationalContentStatus({});
  const data = useLagoon('ui-messaging')();
  const educationalContentModalTitle = find(data, {
    key: ConstantsLagoon.EDUCATIONAL_CONTENT_MODAL
      .EDUCATIONAL_CONTENT_MODAL_TITLE,
  });
  const educationalContentModalDescription = find(data, {
    key: ConstantsLagoon.EDUCATIONAL_CONTENT_MODAL
      .EDUCATIONAL_CONTENT_MODAL_DESCRIPTION,
  });
  const setEducationalContentStore = useStore(
    StoreKeys.SET_EDUCATIONAL_CONTENT_STATUS
  );
  const educationalContentStore = useStore(
    StoreKeys.EDUCATIONAL_CONTENT_STATUS
  );
  const hsidUuid = useHsidUuid();
  const educationalContentStatusArgs = {
    hasViewedContent: true,
    isSuperUser,
  };
  const [, setCorrelationIDSessionStorage] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.CORRELATION_ID_HEADER,
    ''
  );
  const priorityList = useLagoon(
    Constants.LAGOON_TABLE.POPOVER_PRIORITIES
  )() as PriorityItem[];
  const priorityListAllItems =
    priorityList?.map(({ key, duration, releaseDate }) => ({
      key: PopoverKey[key],
      duration: duration ? parseInt(duration.toString(), 10) : 0,
      releaseDate,
    })) || [];

  const [educationalContent, getEducationalContentStatus] =
    useEducationalContentStatus({
      onCompleted: (data) => {
        data?.headers[OPTUM_CORRELATION_ID_HEADER] &&
          setCorrelationIDSessionStorage(
            data?.headers[OPTUM_CORRELATION_ID_HEADER]
          );
      },
    });

  const educationalContentRef = useRef(educationalContent);
  educationalContentRef.current = educationalContent;

  const educationalContentStatus =
    educationalContent?.data?.educationalContentStatus;

  const introCreatedDate: Date = new Date(
    educationalContentStatus?.createdDate
  );
  const todayDate: Date = DateTime.getCurrentDateTime();
  const timeDiff = todayDate.getTime() - introCreatedDate.getTime();
  const memberLoginPeriod: number = Math.floor(
    timeDiff / (1000 * 60 * 60 * 24)
  );
  const hasViewedIntroVideo =
    memberLoginPeriod > 180 && educationalContentStatus?.createdDate !== null
      ? true
      : educationalContentStatus?.hasViewedContent;

  useEffect(() => {
    if (
      !educationalContentStatus?.hasViewedContent &&
      memberLoginPeriod > 180 &&
      educationalContentStatus?.createdDate !== null
    ) {
      setEducationalContentStatus({
        variables: {
          ...educationalContentStatusArgs,
          ...(hsidUuid && { userId: hsidUuid }),
        },
      });
    }
  }, [hasViewedIntroVideo]);

  useEffect(() => {
    if (isEmpty(educationalContentStore)) {
      getEducationalContentStatus({
        variables: {
          isSuperUser,
          ...(hsidUuid && { userId: hsidUuid }),
          ...(isPopOverMessageEnabled && {
            priorityListData: priorityListAllItems,
          }),
        },
      });
    }
    const handlePopoverPriorityChange = async () => {
      const currentEducationalContent = educationalContentRef.current;
      if (currentEducationalContent?.data?.educationalContentStatus) {
        updatePopoverPriority(
          currentEducationalContent,
          priorityList,
          isSuperUser
        );
        setEducationalContentStore(
          currentEducationalContent?.data?.educationalContentStatus
        );
      }
    };
    const handleEventChange = async () => {
      const tempIsSuperUser = storage.session.get(
        Constants.STORAGE_KEYS.SESSION.SUPER_USER
      );
      await getEducationalContentStatus({
        variables: {
          isSuperUser: tempIsSuperUser,
          ...(isPopOverMessageEnabled && {
            priorityListData: priorityListAllItems,
          }),
          ...(hsidUuid && { userId: hsidUuid }),
        },
      });
      setIsSuperUser(tempIsSuperUser);
    };
    window.addEventListener('updateSuperUser', handleEventChange);
    window.addEventListener(
      'popoverPriorityUpdated',
      handlePopoverPriorityChange
    );

    return () => {
      window.removeEventListener('updateSuperUser', handleEventChange);
      window.removeEventListener(
        'popoverPriorityUpdated',
        handlePopoverPriorityChange
      );
    };
  }, []);

  useEffect(() => {
    if (
      educationalContent?.data?.educationalContentStatus &&
      isEmpty(educationalContentStore) &&
      !isSuperUser
    ) {
      const { educationalContentStatus } = educationalContent?.data;
      setEducationalContentStore({
        hasViewedContent: educationalContentStatus?.hasViewedContent,
        showCompareModal: educationalContentStatus?.showCompareModal,
        showCompareCoachmarkWeb:
          educationalContentStatus?.showCompareCoachmarkWeb,
        hasViewedNonGatedPcpSuppressionBanner:
          educationalContentStatus?.hasViewedNonGatedPcpSuppressionBanner,
      });
    }
  }, [educationalContent]);

  useEffect(() => {
    if (educationalContent?.data?.educationalContentStatus && isSuperUser) {
      const { educationalContentStatus } = educationalContent?.data;
      updateEducationalContentStore(educationalContentStatus);
    }
  }, [isSuperUser]);

  const updateEducationalContentStore = (educationalContentStatus) => {
    setEducationalContentStore({
      hasViewedContent: educationalContentStatus?.hasViewedContent,
      showCompareModal: educationalContentStatus?.showCompareModal,
      showCompareCoachmarkWeb:
        educationalContentStatus?.showCompareCoachmarkWeb,
      hasViewedNonGatedPcpSuppressionBanner:
        educationalContentStatus?.hasViewedNonGatedPcpSuppressionBanner,
    });
  };
  const lastUpdatedDate = new Date(educationalContentStatus?.lastUpdatedDate);
  const formatedCurrentDate = DateTime.getCurrentDateTime()
    .toISOString()
    .slice(0, 10);

  const modalIsDisplayedToday = () =>
    lastUpdatedDate.toISOString().slice(0, 10) === formatedCurrentDate;

  const educationalContentVariables = {
    hasViewedContent: hasViewedIntroVideo,
    isSuperUser,
  };

  const educationContenEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.EDUCATIONAL_CONTENT_ENABLED
  );

  if (
    educationContenEnabled &&
    hasViewedIntroVideo === false &&
    !modalIsDisplayedToday()
  ) {
    return (
      <React.Fragment>
        <ModalDialog
          css={educationalContentModalStyles}
          data-auto-testid="educational-content-modal"
          data-testid="educational-content-modal"
          isOpen={isOpen}
          onClose={() => {
            if (!isSuperUser) {
              setEducationalContentStatus({
                variables: {
                  ...educationalContentVariables,
                  ...(hsidUuid && { userId: hsidUuid }),
                },
              });
            }
            setIsOpen(false);
          }}
          title={educationalContentModalTitle?.message}
        >
          <StyledDiv css={educationalContentModalInfoStyles}>
            <video
              controls
              crossOrigin="anonymous"
              data-auto-testid="educational-content-video"
              data-testid="educational-content-video"
              height="auto"
              onEnded={() => setIsOpen(false)}
              onPlay={() => {
                if (!isSuperUser) {
                  setEducationalContentStatus({
                    variables: {
                      ...educationalContentStatusArgs,
                      ...(hsidUuid && { userId: hsidUuid }),
                    },
                  });
                }
              }}
              width="100%"
            >
              <source
                src={`${config('CDN_BASE_URL')}/cdn/assets/UHC_PSX.mp4`}
                type="video/mp4"
              />
              <track
                kind="captions"
                label="English"
                src={`${config(
                  'CDN_BASE_URL'
                )}/cdn/assets/UHC_PSX_Video.en_US.vtt`}
                srcLang="en"
              />
            </video>
          </StyledDiv>
          <StyledDiv
            css={educationalContentModalInfoStyles}
            data-auto-testid="educational-content-info"
            data-testid="educational-content-info"
          >
            {educationalContentModalDescription?.message}
          </StyledDiv>
        </ModalDialog>
      </React.Fragment>
    );
  }
  return null;
};
